import { useEffect, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';
import { getDemoPreviewsStatus } from './api';

/**
 * Runtime gate for the demo preview pages (docs/features/demo-previews.md).
 *
 * The SERVER decides (`DemoPreviews` config section → GET api/DemoPreviews/Status): ON in the dev environment,
 * OFF in prod unless App Service settings switch it on for a company, no rebuild needed.
 *
 * Fetched ONCE per signed-in user per page load and shared by every caller (sidebar, search, routes).
 * 🔴 FAILS CLOSED: while loading, on error, or signed out, `enabled` is false.
 *
 * Why this flag and NOT CompanyPageConfig / DemoSurfaceDefaults: page config is default-ENABLED and is a
 * per-tenant Operator switch. Adding the preview paths to DemoSurfaceDefaults would hide them on fresh dev
 * demo tenants (the opposite of what we want on dev) and would do nothing to keep them off prod. The runtime
 * flag is the gate; page config stays untouched for these paths.
 *
 * ⚠️ An affordance, not a boundary. The previews are NOT fixtures-only (CO-856): on a company with generated
 * payroll they read that company's real employees, payroll rows, adjustments and by-id user records (bank
 * details) through the existing read endpoints, which enforce their own access (Hr of that company). Hiding the
 * pages therefore removes a view, never a permission: an Hr user reaches the same data on the product screens.
 * They write nothing to the server. In browser storage they keep no bank detail (IBAN, labour card number) and no
 * payment-file text, but the `.real` workflow state in localStorage still holds the company's loan and
 * salary-advance rows and any imported input rows, keyed by employee user id, plus run-level totals, and it is kept
 * across sign-out (state/store.tsx, utils/storage/clearPreviewStorage.ts).
 */
export interface DemoPreviewsState {
  enabled: boolean;
  loading: boolean;
}

interface Entry {
  userId: string;
  state: DemoPreviewsState;
}

const DENY_LOADING: DemoPreviewsState = { enabled: false, loading: true };
const DENY_DONE: DemoPreviewsState = { enabled: false, loading: false };

let current: Entry | null = null;
let inflightFor: string | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      /* one broken subscriber must not starve the others */
    }
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function ensureFetched(userId: string): void {
  if (current?.userId === userId || inflightFor === userId) return;
  inflightFor = userId;
  getDemoPreviewsStatus()
    .then((res) => {
      const p = res?.payload;
      const ok = res?.status === 200;
      current = {
        userId,
        state: ok && p ? { enabled: p.enabled === true, loading: false } : DENY_DONE,
      };
    })
    .catch(() => {
      current = { userId, state: DENY_DONE };
    })
    .finally(() => {
      if (inflightFor === userId) inflightFor = null;
      emit();
    });
}

export function useDemoPreviews(): DemoPreviewsState {
  const isLoggedIn = useSelector((s: { user?: { isLoggedIn?: boolean } }) => s.user?.isLoggedIn === true);
  const userId = useSelector((s: { user?: { userId?: string } }) => s.user?.userId ?? '');
  const entry = useSyncExternalStore(subscribe, () => current);

  useEffect(() => {
    if (isLoggedIn && userId) ensureFetched(userId);
  }, [isLoggedIn, userId]);

  if (!isLoggedIn || !userId) return DENY_DONE;
  if (!entry || entry.userId !== userId) return DENY_LOADING;
  return entry.state;
}
